/**
 * Widget Configuration Service
 * Handles API calls for widget configuration management
 */
import api from './api';

class WidgetService {
    /**
     * Create a new widget configuration
     * @param {Object} widgetConfig - Widget configuration data
     * @returns {Promise<Object>} Created widget configuration
     */
    async createWidget(widgetConfig) {
        try {
            console.log('Creating widget configuration:', widgetConfig);
            const response = await api.post('/widget-config/create', widgetConfig);
            console.log('Widget created successfully:', response.data);
            return response.data;
        } catch (error) {
            console.error('Error creating widget:', error);
            throw error;
        }
    }

    /**
     * Get all widget configurations for the current user
     * @returns {Promise<Array>} List of widget configurations
     */
    async getUserWidgets() {
        try {
            console.log('Fetching user widget configurations');
            const response = await api.get('/widget-config/list');
            console.log('User widgets fetched:', response.data);
            return response.data || [];
        } catch (error) {
            console.error('Error fetching user widgets:', error);
            throw error;
        }
    }

    /**
     * Get detailed widget configuration by chatbot_id
     * @param {string} chatbotId - Unique identifier for the chatbot
     * @returns {Promise<Object>} Detailed widget configuration
     */
    async getWidget(chatbotId) {
        try {
            console.log('Fetching widget configuration:', chatbotId);
            const response = await api.get(`/widget-config/${chatbotId}`);
            console.log('Widget configuration fetched:', response.data);
            return response.data;
        } catch (error) {
            console.error('Error fetching widget configuration:', error);
            throw error;
        }
    }

    /**
     * Update widget configuration
     * @param {string} chatbotId - Unique identifier for the chatbot
     * @param {Object} widgetConfig - Updated widget configuration data
     * @returns {Promise<Object>} Update result
     */
    async updateWidget(chatbotId, widgetConfig) {
        try {
            const response = await api.put(`/widget-config/${chatbotId}`, widgetConfig);
            return response.data;
        } catch (error) {
            console.error('Error updating widget:', error);
            throw error;
        }
    }

    /**
     * Delete widget configuration
     * @param {string} chatbotId - Unique identifier for the chatbot
     * @returns {Promise<Object>} Deletion result
     */
    async deleteWidget(chatbotId) {
        try {
            const response = await api.delete(`/widget-config/${chatbotId}`);
            return response.data;
        } catch (error) {
            console.error('Error deleting widget:', error);
            throw error;
        }
    }

    /**
     * Generate JavaScript integration script for a widget
     * @param {string} chatbotId - Unique identifier for the chatbot
     * @param {Object} customConfig - Optional custom configuration overrides
     * @returns {Promise<Object>} Generated script
     */
    async generateScript(chatbotId, customConfig = {}) {
        try {
            console.log('Generating widget script:', chatbotId, customConfig);
            const params = new URLSearchParams(customConfig).toString();
            const url = `/widget-config/${chatbotId}/script${params ? `?${params}` : ''}`;
            const response = await api.get(url);
            console.log('Widget script generated:', response.data);
            return response.data;
        } catch (error) {
            console.error('Error generating widget script:', error);
            throw error;
        }
    }

    /**
     * Add allowed domains for a widget
     * @param {string} chatbotId - Unique identifier for the chatbot
     * @param {Array<string>} domains - List of domain names to allow
     * @returns {Promise<Object>} Addition result
     */
    async addDomains(chatbotId, domains) {
        try {
            console.log('Adding domains to widget:', chatbotId, domains);
            const response = await api.post(`/widget-config/${chatbotId}/domains`, domains);
            console.log('Domains added successfully:', response.data);
            return response.data;
        } catch (error) {
            console.error('Error adding domains:', error);
            throw error;
        }
    }
}

// Export singleton instance
const widgetService = new WidgetService();
export default widgetService;

